import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Report a bug, or edit one.
 *
 * Short on purpose. There is no severity, no priority, no component, and no
 * estimate: the only things asked for are what broke and, optionally, who is
 * looking at it. Everything else is a decision, and decisions are made in triage
 * by people looking at the whole pool rather than by whoever happened to hit the
 * problem.
 */
const BugDialog = ({
  open,
  bug = null,
  candidates = [],
  jiraConnected = false,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState(null);
  const [pushToJira, setPushToJira] = useState(true);

  useEffect(() => {
    if (!open) return;
    setTitle(bug?.title || '');
    setDescription(bug?.description || '');
    setAssignee(
      bug?.assignee
        ? candidates.find(c => c.userId === bug.assignee.userId) || {
          userId: bug.assignee.userId,
          name: bug.assignee.name,
          email: bug.assignee.email,
        }
        : null
    );
    setPushToJira(jiraConnected);
  }, [open, bug, jiraConnected, candidates]);

  const submit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      assigneeUserId: assignee?.userId || null,
      ...(bug ? {} : { pushToJira: jiraConnected && pushToJira }),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{bug ? 'Edit bug' : 'Report a bug'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="What is broken"
          placeholder="Checkout throws on retry with a saved card"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
        <TextField
          fullWidth
          margin="dense"
          multiline
          minRows={4}
          label="How to see it"
          placeholder="Steps, browser, who it affects — whatever the next person needs to reproduce it."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
        />

        <Autocomplete
          sx={{ mt: 2 }}
          options={candidates}
          value={assignee}
          onChange={(e, next) => setAssignee(next)}
          disabled={saving}
          getOptionLabel={(option) => option.name || option.email || 'Unknown'}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Looking at it (optional)"
              helperText="Usually left empty. Bugs get claimed in cool-down rather than handed out."
            />
          )}
        />

        {!bug && jiraConnected && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={pushToJira}
                  onChange={(e) => setPushToJira(e.target.checked)}
                  disabled={saving}
                />
              )}
              label="File this in Jira too"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Keeps the ticket where the rest of your organization looks for one. It
              is saved here either way.
            </Typography>
          </Box>
        )}

        {!bug && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Lands untriaged. Nobody has to pick a priority, because there isn't one —
            the next step is a decision about what to do with it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !title.trim()}>
          {bug ? 'Save' : 'Report it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugDialog;
